import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import BancontactForm from '../../forms/inputs/BancontactForm';
import CreditCardList from './CreditCardList';
import translate from '../../translate';
import Notifications from '../../../services/notifications';
import {
  compose,
  FREQUENCIES_TYPES,
  PAYMENT_METHOD,
} from '../../../services/utilityFunctions';
import withCSRF from '../../withCSRF';
import StripeCardProp from '../../../sections/profile/propTypes/StripeCardProp';
import TenantProp from '../../propTypes/tenantProp';
import VatTypesProp from '../../forms/Address/VatTypesProp';
import PaymentByCompanyInfo from './PaymentByCompanyInfo';
import AddressProp from '../../propTypes/addressProp';
import {
  handleAddCreditCard,
  handleRemoveCreditCard,
} from '../../../services/checkoutHelper';
import { PAYPAL_SUPPORTED_CURRENCIES } from '../../../services/paypalHelper';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import OrderProp from '../../../sections/shop/propTypes/orderProp';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import i18n from '../../../services/i18n';

const klarnaBusinessCountries = [
  'AU',
  'AT',
  'BE',
  'CA',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'NZ',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'GB',
  'US',
];
const klarnaCustomerCountries = [
  'AU',
  'AT',
  'BE',
  'CA',
  'CZ',
  'DK',
  'FI',
  'FR',
  'GR',
  'DE',
  'IE',
  'IT',
  'NL',
  'NZ',
  'NO',
  'PL',
  'PT',
  'RO',
  'ES',
  'SE',
  'CH',
  'GB',
  'US',
];
const klarnaCurrencies = [
  'EUR',
  'USD',
  'GBP',
  'SEK',
  'NOK',
  'DKK',
  'CHF',
  'PLN',
  'CZK',
  'AUD',
  'NZD',
  'CAD',
  'RON',
];

const isKlarnaSupported = (
  customerCountry,
  currency,
  businessCountry,
  paymentMethodCountriesAvailability = [],
) => {
  if (!customerCountry || !currency || !businessCountry) {
    return false;
  }

  if (paymentMethodCountriesAvailability?.length > 0) {
    return (
      klarnaBusinessCountries.includes(businessCountry.toUpperCase()) &&
      paymentMethodCountriesAvailability.includes(
        customerCountry.toUpperCase(),
      ) &&
      klarnaCurrencies.includes(currency.toUpperCase())
    );
  }

  return (
    klarnaBusinessCountries.includes(businessCountry.toUpperCase()) &&
    klarnaCustomerCountries.includes(customerCountry.toUpperCase()) &&
    klarnaCurrencies.includes(currency.toUpperCase())
  );
};

const isAvailableForTotal = (organization, paymentMethod, orderTotal) => {
  const paymentMethodMinAmount =
    organization?.settings?.paymentMethodMinAmount || [];

  const existingPaymentMethodMinAmount = paymentMethodMinAmount.find(
    p => p.type === paymentMethod,
  );

  if (!existingPaymentMethodMinAmount || !orderTotal) {
    return true;
  }

  return existingPaymentMethodMinAmount?.minAmount <= orderTotal;
};

function PaymentMethods({
  t,
  _csrf,
  cards,
  selectedCreditCard,
  accountHolderName,
  tabActive,
  updatePaymentMethod,
  tenant,
  vatTypes,
  companyBilling,
  handleIsLoading,
  isAcademy,
  isSubscription,
  showSaveCard,
  onRemoveCard,
  hidePaypal,
  order,
  orderTotal,
  isUserPlan,
}) {
  const { businessCountry, organization, invoiceMarketplace } =
    useContext(ConfigsContext);
  const { bankAccount } = invoiceMarketplace?.bankAccount || {
    bankAccount: { additionalInfo: null },
  };
  const { paymentMethodCountriesAvailability } = organization?.settings || {};

  const { loggedUser } = useContext(LoggedUserContext);
  const { currency: userCurrency } = loggedUser;
  const vatTypesList = useMemo(
    () =>
      vatTypes.map(c => ({
        id: c.taxCode,
        text:
          c.taxType === 'eu_vat'
            ? `${c.flagEmoji} ${c.countryName} VAT number`
            : `${c.flagEmoji} ${c.name}`,
      })),
    [vatTypes],
  );

  const { settings = {} } = tenant;
  const { paymentMethods = [] } = settings;

  /** HANDLE FUNCTIONS */
  // Callback to parent component
  const handleChangePaymentMethod = (type, payload) => {
    updatePaymentMethod(type, payload);
  };

  // Select and unselect card
  const handleSelectCard = creditCard => {
    handleChangePaymentMethod('updatedCardSelected', {
      cardId: creditCard.id === selectedCreditCard ? null : creditCard.id,
    });
  };

  /** API REQUESTS */
  // Handle Card requests add and delete
  const handleFinishAddCard = async result => {
    try {
      handleIsLoading(true);
      const response = await handleAddCreditCard(
        _csrf,
        isAcademy,
        result.token,
        result.attachToUser,
        result.isPaymentMethod,
      );
      handleChangePaymentMethod('updatedUserCards', {
        cards: cards.concat(response),
        cardId: response.id,
      });
      handleIsLoading(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err);
      console.log(err);
      handleIsLoading(false);
    }
  };
  const handleRemoveCard = async cardId => {
    handleIsLoading(true);
    try {
      await handleRemoveCreditCard(_csrf, cardId, isAcademy);
      handleChangePaymentMethod('removeCreditCard', {
        cards: cards.filter(c => c.id !== cardId),
      });
      onRemoveCard();
      handleIsLoading(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err);
      handleIsLoading(false);
    }
  };

  const isAvailableForFees = type => {
    const orderFeePercentage = order?.fee?.feePercentage || null;
    const organizationMethodFeesByFeePercentage = (
      organization?.settings?.paymentMethodFees ?? []
    ).filter(fee => fee?.feePercentage === orderFeePercentage);
    // if there's no paymentId we can show all methods available as this is only for paid orders
    if (!order?.paymentId) {
      return true;
    }
    // otherwise we need to check if the method is available for the fee percentage
    return (
      !!organizationMethodFeesByFeePercentage.find(fee => fee?.type === type) ||
      type === order.paymentMethodType
    );
  };

  const isCreditCardAvailable =
    paymentMethods.includes(PAYMENT_METHOD.CREDIT_CARD) &&
    isAvailableForTotal(organization, PAYMENT_METHOD.CREDIT_CARD, orderTotal) &&
    isAvailableForFees(PAYMENT_METHOD.CREDIT_CARD);
  const isBancontactAvailable =
    !isSubscription &&
    paymentMethods.includes(PAYMENT_METHOD.BANCONTACT) &&
    isAvailableForTotal(organization, PAYMENT_METHOD.BANCONTACT, orderTotal) &&
    isAvailableForFees(PAYMENT_METHOD.BANCONTACT);

  // Klarna can never be used for subscriptions: Stripe does not allow off-session
  // Klarna confirmation, so the renewal cron cannot charge it.
  const isKlarnaAvailable =
    !isSubscription &&
    !hidePaypal &&
    isKlarnaSupported(
      order?.billingAddress?.countryCode,
      order?.currency,
      businessCountry,
      paymentMethodCountriesAvailability?.find(
        p => p.type === PAYMENT_METHOD.KLARNA,
      )?.allowedCountries || [],
    ) &&
    paymentMethods.includes(PAYMENT_METHOD.KLARNA) &&
    isAvailableForTotal(organization, PAYMENT_METHOD.KLARNA, orderTotal) &&
    isAvailableForFees(PAYMENT_METHOD.KLARNA);

  const isBankTransferAvailable =
    !isSubscription &&
    paymentMethods.includes(PAYMENT_METHOD.BANK_TRANSFER) &&
    isAvailableForTotal(
      organization,
      PAYMENT_METHOD.BANK_TRANSFER,
      orderTotal,
    ) &&
    isAvailableForFees(PAYMENT_METHOD.BANK_TRANSFER);
  const isChequeAvailable =
    !isSubscription &&
    paymentMethods.includes(PAYMENT_METHOD.CHEQUE) &&
    isAvailableForTotal(organization, PAYMENT_METHOD.CHEQUE, orderTotal) &&
    isAvailableForFees(PAYMENT_METHOD.CHEQUE);
  const isCryptoManualAvailable =
    !isSubscription &&
    paymentMethods.includes(PAYMENT_METHOD.CRYPTO_MANUAL) &&
    isAvailableForTotal(
      organization,
      PAYMENT_METHOD.CRYPTO_MANUAL,
      orderTotal,
    ) &&
    isAvailableForFees(PAYMENT_METHOD.CRYPTO_MANUAL);
  const isPaidByCompanyAvailable =
    !isSubscription &&
    paymentMethods.includes(PAYMENT_METHOD.PAID_BY_COMPANY) &&
    isAvailableForTotal(
      organization,
      PAYMENT_METHOD.PAID_BY_COMPANY,
      orderTotal,
    ) &&
    isAvailableForFees(PAYMENT_METHOD.PAID_BY_COMPANY);

  const isCurrencyAvailableForPaypal =
    userCurrency &&
    PAYPAL_SUPPORTED_CURRENCIES.includes(userCurrency.toUpperCase());
  const isPaypalAvailable =
    paymentMethods.includes(PAYMENT_METHOD.PAYPAL) &&
    !hidePaypal &&
    isAvailableForTotal(organization, PAYMENT_METHOD.PAYPAL, orderTotal) &&
    isAvailableForFees(PAYMENT_METHOD.PAYPAL);

  const paymentMethodsEmpty = isSubscription
    ? !isCreditCardAvailable &&
      hidePaypal &&
      !isKlarnaAvailable &&
      !isPaidByCompanyAvailable
    : paymentMethods.length === 0;
  const isCompanyBilling =
    companyBilling?.emails?.length > 0 && companyBilling?.address?.address_1;

  return (
    <div className="payment-methods-checkout">
      <h4>{t('checkout.payment.header')}</h4>
      <div className="subtitle">{t('shop.choose.payment')}</div>
      {paymentMethodsEmpty && <h6>{t('paymentMethods.none.configured')}</h6>}
      <ul id="paymentList" className="nav nav-pills nav-fill" role="tablist">
        {isCreditCardAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method credit-card nav-link show ${
                tabActive === PAYMENT_METHOD.CREDIT_CARD ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.CREDIT_CARD,
                })
              }
            >
              <img id="creditCardImg" src="/images/shop/card.svg" alt="" />
              <span>{t('card')}</span>
            </button>
          </li>
        )}
        {isPaypalAvailable && !isCompanyBilling && (
          <li className="nav-item paypal-tab-container">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.PAYPAL ? 'active' : ''
              }`}
              disabled={!isCurrencyAvailableForPaypal}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.PAYPAL,
                })
              }
            >
              <img src="/images/shop/paypal.svg" alt="" />
              <span>{t('shop.paypal')}</span>
            </button>
            {!isCurrencyAvailableForPaypal ? (
              <span className="tooltip-text">
                {t('tooltip.paypal.currency')}
              </span>
            ) : null}
          </li>
        )}
        {isBancontactAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.BANCONTACT ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.BANCONTACT,
                })
              }
            >
              <img src="/images/shop/bancontact-logo.svg" alt="" />
              <span>Bancontact</span>
            </button>
          </li>
        )}
        {isBankTransferAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.BANK_TRANSFER ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.BANK_TRANSFER,
                })
              }
            >
              <img src="/images/shop/bank-transfer.svg" alt="" />
              <span>{t('bankTransfer')}</span>
            </button>
          </li>
        )}
        {isChequeAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.CHEQUE ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.CHEQUE,
                })
              }
            >
              <img src="/images/shop/cheque.svg" alt="" />
              <span>{t('cheque')}</span>
            </button>
          </li>
        )}
        {isCryptoManualAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.CRYPTO_MANUAL ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.CRYPTO_MANUAL,
                })
              }
            >
              <img src="/images/shop/crypto.svg" alt="" />
              <span>{t('crypto_manual')}</span>
            </button>
          </li>
        )}
        {isPaidByCompanyAvailable && isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.PAID_BY_COMPANY ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.PAID_BY_COMPANY,
                })
              }
            >
              <img src="/images/shop/companyBilling.svg" alt="" />
              <span>{t('paid_by_company')}</span>
            </button>
          </li>
        )}

        {isKlarnaAvailable && !isCompanyBilling && (
          <li className="nav-item">
            <button
              type="button"
              className={`btn-payment-method payment-image nav-link show ${
                tabActive === PAYMENT_METHOD.KLARNA ? 'active' : ''
              }`}
              onClick={() =>
                handleChangePaymentMethod('changeTab', {
                  tab: PAYMENT_METHOD.KLARNA,
                })
              }
            >
              <img src="/images/shop/klarna.svg" alt="" />
              <span>Klarna</span>
            </button>
          </li>
        )}
      </ul>
      <div className="tab-content">
        {isCreditCardAvailable && tabActive === PAYMENT_METHOD.CREDIT_CARD && (
          <div
            className={`tab-pane show ${
              tabActive === PAYMENT_METHOD.CREDIT_CARD ? 'active' : ''
            }`}
          >
            <CreditCardList
              cards={cards}
              selectedCreditCard={selectedCreditCard}
              onSelectCard={handleSelectCard}
              onRemoveCard={handleRemoveCard}
              onAddCard={handleFinishAddCard}
              isCheckout
              showSaveCard={showSaveCard}
              isSubscription={isSubscription}
              loggedUser={loggedUser}
              isUserPlan={isUserPlan}
            />
          </div>
        )}
        {isPaypalAvailable && tabActive === PAYMENT_METHOD.PAYPAL && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.PAYPAL ? 'active' : ''
            }`}
          >
            <h3>{t('shop.checkout.payment.paypal.header')}</h3>
            <span>
              {i18n.__WithReact(
                'shop.checkout.payment.bancontact.info',
                <strong>{t('checkout.complete.order')}</strong>,
                'Paypal',
              )}
            </span>
            <div className="margin-top-20" />
          </div>
        )}
        {isBancontactAvailable && tabActive === PAYMENT_METHOD.BANCONTACT && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.BANCONTACT ? 'active' : ''
            }`}
          >
            <h3>{t('shop.checkout.payment.bancontact.header')}</h3>
            <span>
              {i18n.__WithReact(
                'shop.checkout.payment.bancontact.info',
                <strong>{t('checkout.complete.order')}</strong>,
                'Bancontact',
              )}
            </span>
            <div className="margin-top-20">
              <BancontactForm
                onAccountHolder={value =>
                  handleChangePaymentMethod('updateHolderName', {
                    accountHolderName: value,
                  })
                }
                accountName={accountHolderName}
              />
            </div>
          </div>
        )}
        {isBankTransferAvailable &&
          tabActive === PAYMENT_METHOD.BANK_TRANSFER && (
            <div
              className={`bank-transfer-payment tab-pane show ${
                tabActive === PAYMENT_METHOD.BANK_TRANSFER ? 'active' : ''
              }`}
            >
              <h3>{t('shop.checkout.payment.bankTransfer.header')}</h3>
              <span>{t('shop.checkout.payment.bankTransfer.info')}</span>
              {bankAccount?.additionalInfo &&
                bankAccount?.additionalInfo.length > 0 && (
                  <p>{bankAccount?.additionalInfo}</p>
                )}
            </div>
          )}
        {isChequeAvailable && tabActive === PAYMENT_METHOD.CHEQUE && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.CHEQUE ? 'active' : ''
            }`}
          >
            <h3>{t('cheque')}</h3>
            <span>{t('shop.checkout.payment.cheque.info')}</span>
          </div>
        )}
        {isCryptoManualAvailable &&
          tabActive === PAYMENT_METHOD.CRYPTO_MANUAL && (
            <div
              className={`bank-transfer-payment tab-pane show ${
                tabActive === PAYMENT_METHOD.CRYPTO_MANUAL ? 'active' : ''
              }`}
            >
              <h3>{t('crypto_manual')}</h3>
              <span>{t('shop.checkout.payment.crypto.info')}.</span>
            </div>
          )}
        {isPaidByCompanyAvailable &&
          tabActive === PAYMENT_METHOD.PAID_BY_COMPANY && (
            <div
              className={`bank-transfer-payment tab-pane show ${
                tabActive === PAYMENT_METHOD.PAID_BY_COMPANY ? 'active' : ''
              }`}
            >
              <div className="company-info-container">
                <PaymentByCompanyInfo
                  companyBilling={companyBilling}
                  vatTypesList={vatTypesList}
                />
              </div>
              <div className="hint-box-paid_by_company">
                <div className="warning-box">
                  <span>{t('payment.byCompany.hint.email')}</span>
                </div>
              </div>
            </div>
          )}

        {isKlarnaAvailable && tabActive === PAYMENT_METHOD.KLARNA && (
          <div
            className={`bank-transfer-payment tab-pane show ${
              tabActive === PAYMENT_METHOD.KLARNA ? 'active' : ''
            }`}
          >
            <h3>{t('shop.checkout.payment.klarna.header')}</h3>
            <span>
              {i18n.__WithReact(
                'shop.checkout.payment.bancontact.info',
                <strong>{t('checkout.complete.order')}</strong>,
                'Klarna',
              )}
            </span>
            <div className="margin-top-20" />
          </div>
        )}
      </div>
    </div>
  );
}

PaymentMethods.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  updatePaymentMethod: PropTypes.func.isRequired,
  tabActive: PropTypes.string.isRequired,
  accountHolderName: PropTypes.string,
  selectedCreditCard: PropTypes.string,
  handleIsLoading: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  companyBilling: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    emails: PropTypes.arrayOf(PropTypes.string),
    taxCode: PropTypes.string,
    taxType: PropTypes.string,
    vatNumber: PropTypes.string,
    address: PropTypes.shape(AddressProp.propType),
  }),
  isAcademy: PropTypes.bool,
  showSaveCard: PropTypes.bool,
  isSubscription: PropTypes.oneOf(FREQUENCIES_TYPES),
  onRemoveCard: PropTypes.func,
  hidePaypal: PropTypes.bool,
  order: PropTypes.shape(OrderProp.propType),
  orderTotal: PropTypes.number,
  isUserPlan: PropTypes.bool,
};
PaymentMethods.defaultProps = {
  accountHolderName: '',
  selectedCreditCard: '',
  companyBilling: null,
  isAcademy: false,
  isSubscription: null,
  showSaveCard: true,
  onRemoveCard: null,
  hidePaypal: false,
  order: null,
  orderTotal: null,
  isUserPlan: false,
};

export default compose(translate, withCSRF)(PaymentMethods);
